import React, { useState, useContext, useEffect, useId, useRef } from 'react';
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { DataContext } from '../../../../context/DataContext';
import { getToken } from '../../../../api/Account';
import { updateCreditPhotosAsync } from '../../../../api/Credits';
import { SelectorFotos, useAviso } from '../../../../components/base';

/**
 * Diálogo para cambiar las fotos del crédito (igual que el del cliente). Se dibuja solo si
 * `abierto`, así que al cerrarlo y volver a abrirlo empieza vacío. No se cierra con un clic fuera
 * ni con Escape si ya se eligieron fotos, ni mientras guarda.
 */
const CreditPhoto = ({ id, abierto, setEditPhotos, onActualizado }) => {
	if (!abierto) return null;
	return <CambiarFotos id={id} setEditPhotos={setEditPhotos} onActualizado={onActualizado} />;
};

const CambiarFotos = ({ id, setEditPhotos, onActualizado }) => {
	const [selectedFiles, setSelectedFiles] = useState([]);
	const [previews, setPreviews] = useState([]);
	const [intentado, setIntentado] = useState(false);

	const { setIsLoading, setIsUnautorized } = useContext(DataContext);
	const aviso = useAviso();
	const idTitulo = useId();

	// El overlay de carga no bloquea el teclado: el ref corta un segundo envío al instante.
	const enviando = useRef(false);
	const [guardando, setGuardando] = useState(false);

	const token = getToken();

	// Limpiar URLs cuando el componente se desmonte
	useEffect(() => {
		return () => {
			previews.forEach((preview) => URL.revokeObjectURL(preview.preview));
		};
	}, []);

	const onChangeFile = (e) => {
		const files = Array.from(e.target.files);

		if (!files) return;

		const newPreviews = files.map((file) => ({
			file,
			preview: URL.createObjectURL(file)
		}));

		setSelectedFiles(files);
		setPreviews(newPreviews);
		// Permite volver a elegir el mismo archivo después de quitarlo.
		e.target.value = '';
	};

	const removeImage = (index) => {
		URL.revokeObjectURL(previews[index].preview);
		setPreviews(previews.filter((_, i) => i !== index));
		setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
	};

	// Sin fotos el servidor no toca nada (antes el crédito se quedaba sin fotos): se avisa aquí.
	const errorFotos = intentado && selectedFiles.length === 0 ? 'Elija al menos una foto' : '';

	const saveFotos = async () => {
		setIntentado(true);
		if (selectedFiles.length === 0 || enviando.current) {
			return;
		}
		enviando.current = true;
		setGuardando(true);
		const formData = new FormData();

		formData.append('id', id);

		selectedFiles.forEach((photo) => {
			formData.append('creditPhotos', photo);
		});

		setIsLoading(true);
		let result;
		try {
			result = await updateCreditPhotosAsync(token, formData);
		} catch (e) {
			enviando.current = false;
			setGuardando(false);
			setIsLoading(false);
			aviso.error('No se pudo conectar con el servidor. Inténtalo de nuevo.');
			return;
		}
		enviando.current = false;
		setGuardando(false);
		setIsLoading(false);
		if (!result.statusResponse) {
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			aviso.error(result.error || 'No se pudieron guardar las fotos');
			return;
		}

		onActualizado();
		setEditPhotos(false);
		aviso.exito('Fotos actualizadas');
	};

	const cerrar = (_evento, razon) => {
		// Mientras guarda no se cierra; un clic fuera no debe perder las fotos ya elegidas.
		if (guardando) return;
		if ((razon === 'backdropClick' || razon === 'escapeKeyDown') && selectedFiles.length > 0)
			return;
		setEditPhotos(false);
	};

	return (
		<Dialog
			open
			onClose={cerrar}
			fullWidth
			maxWidth="sm"
			aria-labelledby={`${idTitulo}-titulo`}
			sx={{
				'& .MuiDialog-paper': {
					m: { xs: 1.5, sm: 4 },
					maxWidth: { xs: 'calc(100% - 24px)', sm: 560 }
				}
			}}
		>
			<DialogTitle
				id={`${idTitulo}-titulo`}
				component="h2"
				sx={{ fontWeight: 700, color: 'primary.main', pr: 7 }}
			>
				Cambiar fotos del crédito
			</DialogTitle>
			<IconButton
				aria-label="Cerrar"
				onClick={() => cerrar()}
				disabled={guardando}
				sx={{
					position: 'absolute',
					top: (theme) => theme.spacing(1.5),
					right: (theme) => theme.spacing(1.5),
					color: 'text.secondary'
				}}
			>
				<CloseIcon />
			</IconButton>
			<DialogContent>
				<Typography variant="body2" sx={{ color: 'text.secondary' }}>
					Las fotos que elija reemplazan a las fotos actuales del crédito.
				</Typography>
				<SelectorFotos
					previews={previews}
					onChange={onChangeFile}
					onRemove={removeImage}
					error={errorFotos}
					disabled={guardando}
				/>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={() => cerrar()} disabled={guardando}>
					Cancelar
				</Button>
				<Button
					variant="contained"
					size="large"
					disabled={guardando}
					onClick={() => saveFotos()}
					startIcon={guardando ? <CircularProgress size={18} color="inherit" /> : null}
				>
					{guardando ? 'Guardando…' : 'Guardar fotos'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default CreditPhoto;
